import { CATEGORIES, type Category } from './constants';

/** What POST /api/closetly/import suggests for a shop link. */
export interface ImportedProduct {
  name: string;
  brand: string;
  shop: string;
  price_cents: number;
  currency: string;
  color: string;
  image_url: string;
  category: string;
  url: string;
}

export interface ImportResult {
  product: ImportedProduct;
  /** "page": read from the shop page; "link": only what the link itself says. */
  source: 'page' | 'link';
  reason?: 'blocked' | 'unreachable' | 'no_product';
}

export function importedCategory(p: ImportedProduct): Category | null {
  return (CATEGORIES as readonly string[]).includes(p.category) ? (p.category as Category) : null;
}

/** One line under the link field explaining what was filled in. */
export function importHint(r: ImportResult): string {
  const shop = r.product.shop || 'Der Shop';
  if (r.source === 'page')
    return r.product.price_cents
      ? 'Aus dem Shop übernommen. Prüf kurz Größe und Farbe.'
      : 'Aus dem Shop übernommen, aber ohne Preis. Trag ihn selbst ein.';
  const tip = ' Mit dem Browser-Knopf holst du alles direkt aus der geöffneten Seite.';
  switch (r.reason) {
    case 'blocked':
      return `${shop} lässt keine automatische Abfrage zu. Name und Shop kommen aus dem Link.${tip}`;
    case 'no_product':
      return `Auf der Seite standen keine Produktdaten. Name und Shop kommen aus dem Link.${tip}`;
    default:
      return `Die Seite war gerade nicht erreichbar. Name und Shop kommen aus dem Link.${tip}`;
  }
}

/** The first web link in a text, e.g. what a shop app shares. */
export function firstUrl(text: string | null | undefined): string | null {
  const m = text?.match(/https?:\/\/[^\s<>"']+/);
  return m ? m[0] : null;
}

/** What the browser button collects from a shop page the user has open. */
export interface PagePayload {
  url: string;
  title: string;
  canonical: string;
  meta: Record<string, string>;
  jsonld: string[];
}

/** Reads the `#page=…` part the browser button opens Closetly with (base64url JSON). */
export function decodePagePayload(encoded: string): PagePayload | null {
  try {
    const bin = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
    const data = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))));
    if (typeof data?.url !== 'string') return null;
    return {
      url: data.url,
      title: typeof data.title === 'string' ? data.title : '',
      canonical: typeof data.canonical === 'string' ? data.canonical : '',
      meta: data.meta && typeof data.meta === 'object' ? data.meta : {},
      jsonld: Array.isArray(data.jsonld) ? data.jsonld.filter((j: unknown) => typeof j === 'string') : [],
    };
  } catch {
    return null;
  }
}
